<script setup lang="ts">
import {deleteLegalEntities, updateLegalEntities} from "~/composables/api/profile/legal_entites";

const t = useI18n().t
const emit = defineEmits(['changed']);

const props = defineProps({
  legalEntity: {
    type: Object as () => LegalEntitiesInterface,
    default: () => ({
      legal_entity_id: null,
      email: '',
      phone_number: '+7',
      name: '',
      bank: '',
      address: '',
      bin: '',
      account_number: '',
      bic: '',
    })
  }
})

const form = ref<LegalEntitiesInterface>(Object.assign({},  props.legalEntity))

watch(() => props.legalEntity, (value) => {
  form.value = Object.assign({}, value)
})

const formErrors = ref({
  email: '',
  phone_number: '',
  name: '',
  bank: '',
  address: '',
  bin: '',
  account_number: '',
  bic: '',
})

function clearFormErrors() {
  formErrors.value = {
    email: '',
    phone_number: '',
    name: '',
    bank: '',
    address: '',
    bin: '',
    account_number: '',
    bic: '',
  }
}

function validateForm() {
  const formValue = form.value
  const validated = ref(true)
  if (formValue.phone_number.length < 9) {
    formErrors.value.phone_number = t('validation.validatePhoneNumber')
    validated.value = false
  }
  if (!formValue.email) {
    formErrors.value.email = t('validation.required.just').replace(':attribute', t('contacts.contacts_page.email'))
    validated.value = false
  }
  if (!formValue.name) {
    formErrors.value.name = t('validation.required.just').replace(':attribute', t('legal_entity.name'))
    validated.value = false
  }
  if (!formValue.bin) {
    formErrors.value.bin = t('validation.required.just').replace(':attribute', t('legal_entity.bin'))
    validated.value = false
  }
  if (!formValue.address) {
    formErrors.value.address = t('validation.required.just').replace(':attribute', t('legal_entity.address'))
    validated.value = false
  }
  if (!formValue?.bank) {
    formErrors.value.bank = t('validation.required.just').replace(':attribute', t('legal_entity.bank'))
    validated.value = false
  }
  if (!formValue.account_number) {
    formErrors.value.account_number = t('validation.required.just').replace(':attribute', t('legal_entity.account_number'))
    validated.value = false
  }
  if (!formValue.bic) {
    formErrors.value.bic = t('validation.required.just').replace(':attribute', t('legal_entity.bic'))
    validated.value = false
  }
  return validated.value
}

const {$toast, $toastError} = useNuxtApp()

async function updateLegalEntity() {
  clearFormErrors()
  const validated = validateForm()
  if (!validated) {
    return
  }
  form.value.phone_number = form.value.phone_number.replace(/\D/g, '')
  const {data: response, error, pending} = await updateLegalEntities(form.value.legal_entity_id, form.value)
  if (error.value) {
    Object.entries(error.value.data.errors).forEach((err) => {
      if (err[0]) {
        formErrors.value[err[0]] = err[1][0]
      }
    })
  }
  if (response.value) {
    $toast(t('messages.success.store'))
    emit('changed')
  }
}
async function handleDeleteEntity() {
  const {data: response, error, pending} = await deleteLegalEntities(props.legalEntity?.legal_entity_id)
  if (response.value) {
    emit('changed')
    $toast(t('messages.success.delete'))
  } else {
    $toastError(t('some_problems.title'))
  }
}

const handleTyping = (key: string) => {
  formErrors.value[key] = ''
}
</script>

<template>
  <form @submit.prevent="updateLegalEntity">
    <div class="flex gap-2 flex-col">
      <div class="flex flex-row flex-wrap gap-2">
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px]"
              type="email"
              :placeholder="$t('contacts.contacts_page.email')"
              id="email"
              v-model="form.email"
              :required="true"
              @input="handleTyping('email')"
              name="email"

          />
          <p v-if="formErrors.email" class="ml-2 mt-1 error-text">{{ formErrors.email }}</p>
        </div>
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px]"
              type="tel"
              :placeholder="$t('contacts.contacts_page.phone')"
              mask="+7 (###) ###-##-##"
              id="phone_number"
              @input="handleTyping('phone_number')"
              v-model="form.phone_number"
              :required="true"
          />
          <p v-if="formErrors.phone_number" class="ml-2 mt-1 error-text">{{ formErrors.phone_number }}</p>
        </div>
      </div>
      <div class="gap-2 flex flex-row flex-wrap ">
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px]"
              type="text"
              :placeholder="$t('legal_entity.name')"
              id="legal_name"
              v-model="form.name"
              @input="handleTyping('name')"
              :required="true"
          />
          <p v-if="formErrors.name" class="ml-2 mt-1 error-text">{{ formErrors.name }}</p>
        </div>
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px]"
              type="text"
              :placeholder="$t('legal_entity.bank') "
              id="bank_name"
              v-model="form.bank"
              :required="true"
              name="bank_name"
              @input="handleTyping('bank_name')"
          />
          <p v-if="formErrors.bank" class="ml-2 mt-1 error-text">{{ formErrors.bank }}</p>

        </div>
      </div>
      <div class="gap-2 flex flex-row flex-wrap ">
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px]  text-xs"
              type="text"
              :placeholder="$t('legal_entity.bin') "
              id="legal_name"
              mask="############"
              v-model="form.bin"
              :required="true"
              @input="handleTyping('bin')"
          />
          <p v-if="formErrors.bin" class="ml-2 mt-1 error-text">{{ formErrors.bin }}</p>

        </div>
        <div class="relative ">
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px] text-base"
              type="text"
              id="legal_name"
              :placeholder="$t('legal_entity.address')"
              v-model="form.address"
              :required="true"
              @input="handleTyping('address')"
          />
          <p v-if="formErrors.address" class="ml-2 mt-1 error-text">{{ formErrors.address }}</p>

        </div>
      </div>
      <div class="gap-2 flex flex-row flex-wrap ">
        <div>
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full  h-[54px]  text-xs"
              type="text"
              :placeholder="$t('legal_entity.account_number') "
              id="legal_name"
              v-model="form.account_number"
              mask="####################"
              :required="true"
              @input="handleTyping('account_number')"
          />
          <p v-if="formErrors.account_number" class="ml-2 mt-1 error-text">{{ formErrors.account_number }}</p>

        </div>
        <div class="relative ">
          <UIInputAnimated
              class="bg-white drop-shadow-sm  w-full h-[54px] text-base"
              type="text"
              id="legal_name"
              :placeholder="$t('legal_entity.bic')"
              v-model="form.bic"
              :required="true"
              @input="handleTyping('bic')"
          />
          <p v-if="formErrors.bic" class="ml-2 mt-1 error-text">{{ formErrors.bic }}</p>
        </div>
      </div>
    </div>
    <div class="flex flex-row mt-3 flex-wrap gap-2">
      <UIBtn :color="'green'" type="submit"
             class="md:text-base text-sm px-4 rounded-full">
        {{$t('input_forms.button.save') }}
      </UIBtn>
      <UIBtn :color="'red'" type="button" @click="handleDeleteEntity"
             class="md:text-base text-sm px-4 rounded-full">
        {{$t('input_forms.button.delete') }}
      </UIBtn>
    </div>
  </form>
</template>

<style scoped>

</style>